/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import menus from "./menus";
import JumboVerticalNavbar from "@jumbo/components/JumboVerticalNavbar/JumboVerticalNavbar";
import { DrawerHeader } from "@jumbo/components/JumboLayout/style";
import JumboScrollbar from "@jumbo/components/JumboScrollbar";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import useJumboSidebarTheme from "@jumbo/hooks/useJumboSidebarTheme";
import { SIDEBAR_STYLES, SIDEBAR_VIEWS } from "@jumbo/utils/constants/layout";
import Logo from "../../../../shared/Logo";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import Zoom from "@mui/material/Zoom";
import Div from "@jumbo/shared/Div";
import SidebarSkeleton from "./SidebarSkeleton";
import { userType } from "app/helpers/userType";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchNotificationSummary, fetchUnreadCount, markModuleRead } from "app/redux/slice/notificationSlice";
import { url as API_BASE } from "app/services/axios";

const Sidebar = () => {
  const { isMA } = userType();
  const [sidebarView, setSidebarView] = useState(menus || []);
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );
  const dispatch = useDispatch();
  const location = useLocation();

  const moduleNames = useMemo(() => {
    const names = [];
    const walk = (items) => {
      (items || []).forEach((it) => {
        if (it.moduleName) names.push(it.moduleName);
        if (it.children) walk(it.children);
      });
    };
    walk(menus);
    return Array.from(new Set(names));
  }, []);

  // Fetch initial summary counts
  useEffect(() => {
    if (moduleNames.length) {
      dispatch(fetchNotificationSummary(moduleNames));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, moduleNames.length]);

  // Server-Sent Events: live updates without refresh
  useEffect(() => {
    if (!API_BASE) return;
    let es;
    try {
      es = new EventSource(`${API_BASE}/notification/stream`, { withCredentials: true });
      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data || '{}');
          if (data?.moduleName) {
            dispatch(fetchUnreadCount(data.moduleName));
          }
        } catch (_) {}
      };
      es.onerror = () => {
        // Fall back to polling; SSE will auto-reconnect
      };
    } catch (_) {}
    return () => {
      try { es && es.close(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE]);

  // Background polling for live updates and focus-based refresh
  useEffect(() => {
    if (!moduleNames.length) return;

    const refresh = () => dispatch(fetchNotificationSummary(moduleNames));

    // Poll every 15 seconds when tab is visible
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    }, 15000);

    // Refresh when window/tab gains focus
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, moduleNames.length]);
  useEffect(() => {
    if (!isMA && permissionData?.length) {
      const filteredMenus = menus.filter((menu) => {
        // Filter first level children
        const filteredChildren = menu?.children?.filter((ch) => {
          const hasPermission = permissionData.some(
            (perm) => perm?.name === ch?.permission || ch?.permission === "All"
          );

          // Filter sub-children and check if any have permission
          let hasChildPermission = false;

          if (ch.children) {
            // Filter the sub-children array to only include items with permission
            const filteredSubChildren = ch.children.filter((subCh) =>
              permissionData.some(
                (perm) =>
                  perm?.name === subCh?.permission ||
                  subCh?.permission === "All"
              )
            );

            // Update the children array with filtered results
            ch.children = filteredSubChildren;
            // Check if there are any sub-children with permission
            hasChildPermission = filteredSubChildren.length > 0;
          }

          return hasPermission || hasChildPermission;
        });

        menu.children = filteredChildren;
        return filteredChildren.length > 0;
      });

      setSidebarView(filteredMenus);
    }
  }, [!isMA, permissionData, menus]);

  // Mark notifications as read when navigating to a module route
  useEffect(() => {
    const findModuleByPath = () => {
      const path = location.pathname;
      // search through menus for matching uri or isActiveUri
      const stack = [...menus];
      while (stack.length) {
        const item = stack.shift();
        if (!item) continue;
        if (item.children) stack.push(...item.children);
        if (item.type === "nav-item" && item.moduleName) {
          const isActive =
            item.uri === path ||
            (item.isActiveUri &&
              item.isActiveUri.some((e) => {
                const regex = new RegExp(
                  "^" + e.replace(/:[^\s/]+/g, "([\\w-]+)").replace(/\//g, "\\/") + "$"
                );
                return regex.test(path);
              }));
          if (isActive) return item.moduleName;
        }
      }
      return null;
    };

    const moduleName = findModuleByPath();
    if (moduleName) {
      dispatch(markModuleRead(moduleName)).then(() => {
        dispatch(fetchNotificationSummary(moduleNames));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <React.Fragment>
      <SidebarHeader />
      <JumboScrollbar autoHide autoHideDuration={200} autoHideTimeout={500}>
        <Suspense
          fallback={
            <Div
              sx={{
                display: "flex",
                minWidth: 0,
                alignItems: "center",
                alignContent: "center",
                px: 3,
              }}
            >
              <SidebarSkeleton />
            </Div>
          }
        >
          <JumboVerticalNavbar translate items={sidebarView} />
        </Suspense>
      </JumboScrollbar>
    </React.Fragment>
  );
};

const SidebarHeader = () => {
  const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
  const { sidebarTheme } = useJumboSidebarTheme();

  const isMiniAndClosed = React.useMemo(() => {
    return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
  }, [sidebarOptions.view, sidebarOptions.open]);

  return (
    <React.Fragment>
      {sidebarOptions?.style !== SIDEBAR_STYLES.CLIPPED_UNDER_HEADER && (
        <DrawerHeader>
          <Logo mini={isMiniAndClosed} mode={sidebarTheme.type} />
          {sidebarOptions?.view !== SIDEBAR_VIEWS.MINI && (
            <Zoom in={sidebarOptions?.open}>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                sx={{ ml: 0, mr: -1.5 }}
                onClick={() => setSidebarOptions({ open: false })}
              >
                <MenuOpenIcon />
              </IconButton>
            </Zoom>
          )}
        </DrawerHeader>
      )}
    </React.Fragment>
  );
};

export default Sidebar;
